import React from 'react';
import Select from 'react-select';

interface Merchant {
  id: string;
  name: string;
}

interface Props {
  merchants: Merchant[];
  value: string;
  onChange: (id: string) => void;
  variant?: 'dark' | 'light';
  placeholder?: string;
}

const darkStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: 'rgba(255,255,255,0.15)',
    borderColor: state.isFocused ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.3)',
    borderRadius: '0.75rem',
    minWidth: '180px',
    maxWidth: '340px',
    backdropFilter: 'blur(4px)',
    boxShadow: 'none',
    cursor: 'pointer',
  }),
  placeholder: (base: any) => ({ ...base, color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem' }),
  input: (base: any) => ({ ...base, color: 'white', fontSize: '0.875rem' }),
  singleValue: (base: any) => ({ ...base, color: 'white', fontSize: '0.875rem' }),
  menu: (base: any) => ({ ...base, background: '#ffffff', borderRadius: '0.75rem', border: '1px solid #e2e8f0', boxShadow: '0 10px 40px rgba(0,0,0,0.15)', overflow: 'hidden' }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  option: (base: any, state: any) => ({
    ...base,
    background: state.isSelected ? '#eff6ff' : state.isFocused ? '#f8fafc' : '#ffffff',
    color: state.isSelected ? '#2563eb' : '#1e293b',
    fontSize: '0.875rem',
    fontWeight: state.isSelected ? '600' : '400',
    cursor: 'pointer',
  }),
  clearIndicator: (base: any) => ({ ...base, color: 'rgba(255,255,255,0.6)', padding: '4px', '&:hover': { color: 'white' } }),
  dropdownIndicator: (base: any) => ({ ...base, color: 'rgba(255,255,255,0.6)', padding: '4px', '&:hover': { color: 'white' } }),
  indicatorSeparator: (base: any) => ({ ...base, background: 'rgba(255,255,255,0.3)' }),
  noOptionsMessage: (base: any) => ({ ...base, color: '#94a3b8', fontSize: '0.875rem' }),
};

const lightStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: '#ffffff',
    borderColor: state.isFocused ? '#f97316' : '#e5e7eb',
    borderRadius: '0.5rem',
    minWidth: '180px',
    boxShadow: 'none',
    cursor: 'pointer',
    '&:hover': { borderColor: '#d1d5db' },
  }),
  placeholder: (base: any) => ({ ...base, color: '#9ca3af', fontSize: '0.875rem' }),
  input: (base: any) => ({ ...base, color: '#374151', fontSize: '0.875rem' }),
  singleValue: (base: any) => ({ ...base, color: '#374151', fontSize: '0.875rem' }),
  menu: (base: any) => ({ ...base, background: '#ffffff', borderRadius: '0.75rem', border: '1px solid #e2e8f0', boxShadow: '0 10px 40px rgba(0,0,0,0.15)', overflow: 'hidden' }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  option: (base: any, state: any) => ({
    ...base,
    background: state.isSelected ? '#eff6ff' : state.isFocused ? '#f8fafc' : '#ffffff',
    color: state.isSelected ? '#2563eb' : '#1e293b',
    fontSize: '0.875rem',
    fontWeight: state.isSelected ? '600' : '400',
    cursor: 'pointer',
  }),
  clearIndicator: (base: any) => ({ ...base, color: '#9ca3af', padding: '4px', '&:hover': { color: '#374151' } }),
  dropdownIndicator: (base: any) => ({ ...base, color: '#9ca3af', padding: '4px', '&:hover': { color: '#374151' } }),
  indicatorSeparator: (base: any) => ({ ...base, background: '#e5e7eb' }),
  noOptionsMessage: (base: any) => ({ ...base, color: '#94a3b8', fontSize: '0.875rem' }),
};

const MerchantMultiSelect: React.FC<Props> = ({
  merchants,
  value,
  onChange,
  variant = 'dark',
  placeholder = 'ทุก Merchant',
}) => {
  const options = merchants.map((m) => ({ value: m.id, label: m.name }));
  const selected = options.find((o) => o.value === value) ?? null;

  return (
    <Select
      options={options}
      value={selected}
      onChange={(opt: any) => onChange(opt ? opt.value : '')}
      placeholder={placeholder}
      styles={variant === 'dark' ? darkStyles : lightStyles}
      isClearable
      menuPortalTarget={document.body}
      menuPosition="fixed"
      noOptionsMessage={() => 'ไม่พบ Merchant'}
    />
  );
};

export default MerchantMultiSelect;
